import { useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';

// Tecton
import { useGetFcos } from '../../../api/fcos';
import { FCOType, WorkspaceFCOContainer } from '../../../core/types/fcoTypes';
import { useUserSettings } from '../../context/UserSettingsContext';
import { EntityRoutes, getEntityRouteName, getEntityRoutes } from './entitiesUtils';
import EntityCards from './EntityOverview';
import FeatureViewsList from '../featureViews/FeatureViewsList';

// @tecton
import { Loading, Tabs, ViewLayout } from '@lithos';

const EntityContainerOutlet = () => {
  /*
    This contains the header portion of the Feature view
    - Breadcrumbs
    - Tabs
    - Outlet to the content
  */
  const navigation = useNavigate();
  const { name } = useParams();
  const { workspace, workspaceDetails } = useUserSettings();
  const location = useLocation();
  const featureRoutes = getEntityRouteName(location.pathname);
  const [selectedTab, setSelectedTab] = useState<EntityRoutes>(featureRoutes);

  const { data, isLoading } = useGetFcos(workspace ?? '', {
    select: (data: WorkspaceFCOContainer) => {
      return {
        entity: data?.entities.find((entity) => {
          return entity?.name === name;
        }),
        featureViews: data?.featureViews,
      };
    },
  });

  const routeNames = Object.keys(getEntityRoutes());

  const headerProps = {
    breadcrumbs: {
      root: { label: workspace ?? '', url: `/repo/${workspace}` },
      items: [{ label: 'Entities', url: `/repo/${workspace}/entities` }, { label: name ?? '' }],
    },
    title: name,
    tabs: {
      items: routeNames.map((routeName) => ({ label: routeName, value: routeName })),
      selectedTab: selectedTab,
      setSelectedTab: (tab: EntityRoutes) => {
        const routes = getEntityRoutes();
        navigation(routes[tab]);
        setSelectedTab(tab);
      },
    },
  };

  if (isLoading) {
    <ViewLayout header={headerProps} content={<Loading />} />;
  }

  return <ViewLayout header={headerProps} content={<Outlet />} />;
};

const EntityContainer = () => {
  const { workspace } = useUserSettings();
  const { name } = useParams();
  const { data, isLoading } = useGetFcos(workspace ?? '', {
    select: (data: WorkspaceFCOContainer) => {
      return {
        entity: data?.entities.find((entity) => {
          return entity?.name === name;
        }),
        idToFcoMap: data?.idToFcoMap,
      };
    },
  });

  const dependentFeatureViews = data?.entity?.dependentFeatureViews?.map((id: string) => data.idToFcoMap[id]) ?? [];

  if (isLoading) {
    return <></>;
  }

  if (!data?.entity) {
    // We switched workspaces and didn't find the entity name, so we redirect them to the entity lists
    return <Navigate to={`../${workspace}/entities`} />;
  }

  const entityRoutes = getEntityRoutes();

  return (
    <Routes>
      <Route element={<EntityContainerOutlet />}>
        <Route index element={<Navigate to={entityRoutes[`Metadata`]} replace />} />
        <Route path={entityRoutes[`Metadata`]} element={<EntityCards entity={data?.entity} />} />
        <Route
          path={entityRoutes[`Feature Views`]}
          element={
            <>
              <FeatureViewsList
                items={dependentFeatureViews}
                idToFcoMap={data.idToFcoMap}
                workspace={workspace ?? ''}
              />
            </>
          }
        />
      </Route>
    </Routes>
  );
};

export default EntityContainer;
